import { ref, computed, watch, watchEffect, nextTick } from 'vue'
import { onMounted } from 'vue'
import type { InspectionType } from '~/types/quality-control/inspectionType'

interface TreeNode {
  id: number
  label: string
  key: string
  children?: TreeNode[]
}

const CACHE_KEY = 'iqa-data-entry-tree-cache'
const CACHE_KEY1 = 'iqa-data-entry-tree-cache-records'
const CACHE_KEY3 = 'iqa-data-entry-tree-cache-rex3'
const CACHE_KEY4 = 'iqa-data-entry-tree-cache-open-cell'
const CACHE_KEY5 = 'iqa-data-entry-tree-cache-batches'
const CACHE_KEY7 = 'iqa-data-entry-tree-cache-types-only'
const CACHE_KEY8 = 'iqa-data-entry-tree-cache-ai'
const TTL = 1000 * 60 * 60 // 1 hora
// Subir cuando cambien los valores que guardan los nodos (p. ej. el type de una modalidad):
// los cachés de una versión anterior se descartan en vez de usarse hasta que venzan.
const CACHE_VERSION = 2

type CacheKey =
  | typeof CACHE_KEY
  | typeof CACHE_KEY1
  | typeof CACHE_KEY3
  | typeof CACHE_KEY4
  | typeof CACHE_KEY5
  | typeof CACHE_KEY7
  | typeof CACHE_KEY8
function loadFromCache(cacheKeyName: CacheKey): TreeNode[] | null {

  const VALID_CACHE_KEYS = [CACHE_KEY, CACHE_KEY1, CACHE_KEY3, CACHE_KEY4, CACHE_KEY5, CACHE_KEY7, CACHE_KEY8] as const

  function isValidCacheKey(key: string): key is typeof VALID_CACHE_KEYS[number] {
    return VALID_CACHE_KEYS.includes(key as any)
  }

  if (!isValidCacheKey(cacheKeyName)) {
    console.warn(`Invalid cache key: ${cacheKeyName}`)
    return null
  }

  const raw = localStorage.getItem(cacheKeyName)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    const expired = Date.now() - parsed.timestamp > TTL

    if (expired || parsed.version !== CACHE_VERSION) {
      localStorage.removeItem(cacheKeyName)
      return null
    }

    return parsed.data
  } catch {
    localStorage.removeItem(cacheKeyName)
    return null
  }
}

export function useDataEntryTree(allowHidden: boolean = true, locale: any, cacheKeyName: CacheKey) {
  // State
  const showTree = ref(false)
  const treeRef = ref<HTMLElement | null>(null)
  const inputRef = ref<HTMLElement | null>(null)
  const selectedInspectionId = ref<number | null>(null);
  const selectedInspectionName = ref<string | null>(null);

  const openInspectionRex3Form = ref(false);
  const openInspectionOpenCellForm = ref(false);
  const openInspectionBatchForm = ref(false);

  const nodes = ref<TreeNode[]>([])
  const filter = ref('')

  const selectedNodes = ref<Record<string, any>>({})
  const expandedKeys = ref<Record<string, boolean>>({})
  const selectedNodeLabel = ref("")
  const closeOnLeafSelect = true
  const vCollapseUnselectedNodes = true

  // Toggle tree visibility
  function toggleSearchTree() {
    showTree.value = true
  }

  function mapInspectionTypesToTree(data: InspectionType[],prefix = '0'): TreeNode[]
  {
    return data.map((x, index) => {
      const key = `${prefix}-${index}`

      return {
        id: Number(x.id),
        key,
        label: x.name, 
        name: x.name, 
        //se guarda como label porque los componentes de UI esperan esa propiedad, 
        // aunque en BD se llame name. pero en caché se guarda el nombre original name, 
        // para evitar problemas con la lógica de negocio que espera esa propiedad.
        nodeType: x.nodeType,
        type: x.type,
        onSelect: () => {
          selectedInspectionName.value = x.name;
          selectedInspectionId.value = x.id;
          if (x.type == 'rex3')
          {
            openInspectionRex3Form.value = true;
          }
          else if (x.type == 'openCell')
          {
            openInspectionOpenCellForm.value = true;
          }
          else if (x.type == 'batches')
          {
            openInspectionBatchForm.value = true;
          }
        },
        ... (x.nodeType == 'TEMPLATE' && x.type == 'record') ? {to: '/modules/data-entry/inspections/form/new/'+x.id}:
            {},
        icon: 'i-lucide-circle-small',
        ...(x.children && x.children.length > 0
          ? { children: mapInspectionTypesToTree(x.children, key) }
          : {})
      }
    })
  }

  let loadPromise: Promise<void> | null = null

  async function loadNodes(cacheKeyName: CacheKey) {
    
    if (loadPromise) return loadPromise

    loadPromise = (async () => {
      const cached = loadFromCache(cacheKeyName)
      if (cached)
      {  
        const fullTree = mapInspectionTypesToTree(cached as any[]);
        nodes.value = fullTree
        return
      }
      
      const { data: inspectionTypeResponse } = await useApiFetchArray<InspectionType>('/inspectionType/children')
      if (!inspectionTypeResponse.value) throw new Error('No se pudieron obtener el catálogo de inspecciones')

      const fullTree = mapInspectionTypesToTree(
        inspectionTypeResponse.value.data[0]['children'] as InspectionType[]
      )
      //nodes.value = fullTree

      // Guardar cache completo

      // Segmenta caché por tipo de inspección
      const recordTree = filterTreeByType(fullTree, 'record')
      const rex3Tree = filterTreeByType(fullTree, 'rex3')
      const openCellTree = filterTreeByType(fullTree, 'openCell')
      const batchesTree = filterTreeByType(fullTree, 'batches')
      const aiTree = filterTreeByType(fullTree, 'ai')
      const fullTreeTypesOnly = filterTreeOnlyInspectionTypes(fullTree)
      const fullTreefiltered = filterTreeExcludeType(fullTree,'ai')

      // El árbol asignado a nodes.value debe coincidir con el que se lee
      // desde loadFromCache(cacheKeyName) más arriba: si no, la primera
      // carga (sin caché) y las siguientes (con caché) muestran árboles
      // distintos para el mismo cacheKeyName.
      const treesByCacheKey: Record<CacheKey, TreeNode[]> = {
        [CACHE_KEY]: fullTreefiltered,
        [CACHE_KEY1]: recordTree,
        [CACHE_KEY3]: rex3Tree,
        [CACHE_KEY4]: openCellTree,
        [CACHE_KEY5]: batchesTree,
        [CACHE_KEY7]: fullTreeTypesOnly,
        [CACHE_KEY8]: aiTree,
      }

      nodes.value = treesByCacheKey[cacheKeyName] ?? fullTreefiltered //a partir de que se implementa la funcionalidad de inspecciones IA,
      // se filtra el árbol completo para que no se muestren en la lista de inspecciones normales,
      // sino en su propia sección de inspecciones IA.

      // Guardar cachés separados
      saveGenericCache(CACHE_KEY, fullTreefiltered)
      saveGenericCache(CACHE_KEY1, recordTree)
      saveGenericCache(CACHE_KEY3, rex3Tree)
      saveGenericCache(CACHE_KEY4, openCellTree)
      saveGenericCache(CACHE_KEY5, batchesTree)
      saveGenericCache(CACHE_KEY7, fullTreeTypesOnly)
      saveGenericCache(CACHE_KEY8, aiTree) // Guardar caché completo para inspecciones IA
    })();

    return loadPromise
  }

  onMounted(() => loadNodes(cacheKeyName))

  // Watch showTree open/close
  watch(showTree, async (newVal, oldVal) => {
    await nextTick()

    if (!showTree.value && Object.keys(selectedNodes.value).length === 0)
      filter.value = ""

    if (!showTree.value)
      filter.value = selectedNodeLabel.value
  })


  // Fetch items when locale changes
  //descomentar si se desea trabajar con lista estática desarrollo
  /*
  watchEffect(async () => {
    const items = await useDataEntryItems(locale.value)
    nodes.value = items
  })
  */
  // Filtering logic
  const filteredNodes = computed(() => {
    if (!filter.value) return nodes.value

    const filterNode = (node: TreeNode): TreeNode | null => {
      const match = node.label.toLowerCase().includes(filter.value.toLowerCase())

      if (node.children) {
        const filteredChildren = node.children
          .map(filterNode)
          .filter(Boolean) as TreeNode[]

        if (filteredChildren.length)
          return { ...node, children: filteredChildren }
      }

      return match ? node : null
    }

    return nodes.value.map(filterNode).filter(Boolean) as TreeNode[]
  })
  const filteredNodesValue = computed(() => filteredNodes.value)
  // Click outside
  async function handleClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement

    if (target.closest('#btnDataEntrySearchCleaner')) return

    if (
      treeRef.value &&
      inputRef.value &&
      !treeRef.value.contains(target) &&
      !inputRef.value.contains(target) &&
      allowHidden == true
    ) {
      showTree.value = false
    }

    await nextTick()
  }

  // Selected nodes logic
  watch(
    selectedNodes,
    (newSelection, oldSelection) => {
      selectedNodeLabel.value = ""

      const newKey = Object.keys(newSelection)[0] || null
      const oldKey = Object.keys(oldSelection)[0] || null

      // 1) Check if new is ancestor of old
      if (newKey && oldKey) {
        const newNode = findNodeByKey(nodes.value, newKey)
        const oldNode = findNodeByKey(nodes.value, oldKey)

        if (newNode && oldNode && isAncestor(newNode, oldNode)) {
          filter.value = newNode.label
        }
      }

      // 2) Existing selection logic
      const selectedKeys = Object.keys(newSelection)

      selectedKeys.forEach((key) => {
        const node = findNodeByKey(nodes.value, key)
        if (!node) return

        if (node.children && node.children.length > 0) {
          selectedNodeLabel.value = node.label
          expandedKeys.value[key] = true

          if (selectedNodeLabel.value.toLowerCase().includes(filter.value.toLowerCase()) || filter.value === "")
            filter.value = node.label

          fCollapseUnselectedNodes(newSelection)
          return
        }

        selectedNodeLabel.value = node.label

        if (closeOnLeafSelect && allowHidden) showTree.value = false

        filter.value = node.label
      })

      // 3) Collapse unselected nodes
      if (vCollapseUnselectedNodes) {
        fCollapseUnselectedNodes(newSelection)
      }
    },
    { deep: true }
  )

  // Helpers
  function isAncestor(ancestor: any, target: any): boolean {
    if (!ancestor.children) return false

    for (const child of ancestor.children) {
      if (child.key === target.key) return true
      if (isAncestor(child, target)) return true
    }
    return false
  }

  function fCollapseUnselectedNodes(newSelection: Record<string, any>) {
    const selectedKeys = Object.keys(newSelection)
    const selectedSet = new Set(selectedKeys)

    for (const expKey of Object.keys({ ...expandedKeys.value })) {
      const expNode = findNodeByKey(nodes.value, expKey)
      if (!expNode) {
        delete expandedKeys.value[expKey]
        continue
      }

      const hasSelectedDescendant = subtreeHasSelected(expNode, selectedSet)
      if (!hasSelectedDescendant) delete expandedKeys.value[expKey]
    }
  }

  function subtreeHasSelected(node: any, selectedSet: Set<string>): boolean {
    if (!node) return false
    if (node.key && selectedSet.has(node.key)) return true
    if (!node.children) return false

    for (const child of node.children)
      if (subtreeHasSelected(child, selectedSet)) return true

    return false
  }

  function findNodeByKey(nodes: any[], targetKey: string): any | null {
    for (const node of nodes) {
      if (node.key === targetKey) return node
      if (node.children) {
        const found = findNodeByKey(node.children, targetKey)
        if (found) return found
      }
    }
    return null
  }

  // Los ids de TYPE y TEMPLATE vienen de tablas distintas y pueden coincidir:
  // pasar nodeType para no confundir un tipo con un template del mismo id.
  function findNodeById(nodes: any[], targetId: number, nodeType?: 'TYPE' | 'TEMPLATE'): any | null {
    for (const node of nodes) {
      if (Number(node.id) === Number(targetId) && (!nodeType || node.nodeType === nodeType)) return node
      if (node.children) {
        const found = findNodeById(node.children, targetId, nodeType)
        if (found) return found
      }
    }
    return null
  }
  function removeLastLevel(value: string): string {
    const lastDash = value.lastIndexOf('-')

    return lastDash === -1
      ? value
      : value.substring(0, lastDash)
  }
  function markNodeAsSelectedById(nodes: any[], targetId: number, nodeType?: 'TYPE' | 'TEMPLATE'): any | null {
    const nodeToBeMarked = findNodeById(nodes, targetId, nodeType);
    if( nodeToBeMarked  == undefined)
      return false;
    selectedNodes.value = { [nodeToBeMarked.key]: true };

    filter.value = nodeToBeMarked.name;
    filteredNodesValue.value[0] = nodeToBeMarked;
    const parentNote = findNodeByKey(nodes,removeLastLevel(nodeToBeMarked.key));
    if (parentNote) expandedKeys.value = { ...expandedKeys.value, [parentNote.key]: true };
    
    selectedInspectionId.value = targetId;
    selectedInspectionName.value = nodeToBeMarked.name;
    return true;
  }

  function filterTreeOnlyInspectionTypes(nodes: TreeNode[]): TreeNode[] {
    const filterNode = (node: TreeNode): TreeNode | null => {
      const isMatch = (node as any).nodeType === 'TYPE'

      if (node.children) {
        const filteredChildren = node.children
          .map(filterNode)
          .filter(Boolean) as TreeNode[]

        if (filteredChildren.length) {
          return { ...node, children: filteredChildren }
        }
      }

      return isMatch ? { ...node, children: [] } : null
    }

    return nodes.map(filterNode).filter(Boolean) as TreeNode[]
  }
  function filterTreeByType(nodes: TreeNode[], type: string): TreeNode[] {
    const filterNode = (node: TreeNode): TreeNode | null => {
      const isMatch = (node as any).type === type

      if (node.children) {
        const filteredChildren = node.children
          .map(filterNode)
          .filter(Boolean) as TreeNode[]

        if (filteredChildren.length) {
          return { ...node, children: filteredChildren }
        }
      }

      return isMatch ? { ...node, children: [] } : null
    }

    return nodes.map(filterNode).filter(Boolean) as TreeNode[]
  }

  function filterTreeExcludeType(nodes: TreeNode[], type: string): TreeNode[] {
    const filterNode = (node: TreeNode): TreeNode | null => {
      const isMatch = (node as any).type === type
      if(isMatch)
        return null;

      if (node.children) {
        const filteredChildren = node.children
          .map(filterNode)
          .filter(Boolean) as TreeNode[]

        if (filteredChildren.length) {
          return { ...node, children: filteredChildren }
        }
      }

      return { ...node, children: [] } 
    }

    return nodes.map(filterNode).filter(Boolean) as TreeNode[]
  }

  function saveGenericCache(key: CacheKey, data: TreeNode[]) {
    localStorage.setItem(
      key,
      JSON.stringify({
        version: CACHE_VERSION,
        timestamp: Date.now(),
        data
      })
    )
  }
  function expandTopNode() {
    if (!nodes.value.length) return

    const rootKey = nodes.value[0].key
    expandedKeys.value = {
      ...expandedKeys.value,
      [rootKey]: true
    }
  }

  // Expose API
  return {
    // state
    showTree,
    selectedInspectionId,
    selectedInspectionName,
    openInspectionRex3Form,
    openInspectionOpenCellForm,
    openInspectionBatchForm,
    treeRef,
    inputRef,
    nodes,
    filter,
    filteredNodesValue,
    selectedNodes,
    expandedKeys,
    selectedNodeLabel,
    // actions
    toggleSearchTree,
    handleClickOutside,

    // helpers (optional)
    findNodeByKey,
    findNodeById,
    markNodeAsSelectedById,
    expandTopNode,
    loadNodes
  }
}
